import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import LanguageSwitcher from '../LanguageSwitcher';

const Header = () => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    setIsScrolled(window.scrollY > 10);
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    const navigation = [
        { name: t('header.home'), href: '/' },
        { name: t('header.gallery'), href: '/gallery' }, // Added Gallery button
        { name: t('header.contact'), href: '/contact' },
    ];

    const isActive = (href) => {
        if (href === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(href);
    };

    return (
        <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-blue-100'
            : 'bg-white/90 backdrop-blur-sm'
            }`}>
            <nav className="px-4 sm:px-6 md:px-16 py-2">
                <div className="flex items-center justify-between h-14 md:h-16 w-full">
                    {/* Logo - Kompakter für Mobile */}
                    <div className="flex items-center flex-shrink-0">
                        <Link to="/" className="flex items-center group">
                            <div className="relative">
                                <div className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center mr-2 md:mr-3 transition-all duration-300 transform group-hover:scale-105">
                                    <img
                                        src="/favicon.ico"
                                        alt="Sunsite Logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xl md:text-3xl font-bold">
                                    <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Sun</span>
                                    <span className="text-black">site</span>
                                </span>
                                <span className="text-xs md:text-sm text-gray-600 font-medium hidden sm:block">
                                    {t('header.companySlogan')}
                                </span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6 lg:space-x-9">
                        <LanguageSwitcher />
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`px-6 lg:px-9 py-3 lg:py-4 rounded-2xl text-base lg:text-lg font-semibold transition-all duration-200 border border-blue-100 bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isActive(item.href)
                                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-500 border-transparent'
                                    : 'text-slate-700 hover:bg-gray-200 hover:text-slate-800'
                                    }`}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>

                    {/* Mobile Menu Button und Language Switcher */}
                    <div className="flex items-center space-x-3 md:hidden">
                        <div className="scale-90">
                            <LanguageSwitcher />
                        </div>
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <svg
                                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            <svg
                                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="border-t border-blue-100 bg-white/95 backdrop-blur-md">
                        <div className="px-2 pt-3 pb-4 space-y-2">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${isActive(item.href)
                                        ? 'text-white bg-gradient-to-r from-blue-600 to-blue-500'
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100'
                                        }`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;